import React, { useEffect, useMemo, useState } from "react";
import { RotateCw, Star, X } from "lucide-react";

/** Rotate a File 90° clockwise in the browser; returns a new File (JPEG, or PNG if the source is PNG). */
export const rotateFile90 = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      URL.revokeObjectURL(url);
      const isPng = /png$/i.test(file.type);
      canvas.toBlob(
        (blob) => (blob ? resolve(new File([blob], file.name, { type: blob.type, lastModified: Date.now() })) : reject(new Error("rotate failed"))),
        isPng ? "image/png" : "image/jpeg",
        0.92,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load failed")); };
    img.src = url;
  });

/** Thumbnails of the photos selected for upload, each with rotate / remove. */
export const NewPhotosPreview = ({ files, onChange }) => {
  const list = useMemo(() => (Array.isArray(files) ? files : files ? [files] : []), [files]);
  const [busy, setBusy] = useState(null);
  const urls = useMemo(() => list.map((f) => URL.createObjectURL(f)), [list]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);

  if (!list.length) return null;

  const rotate = async (i) => {
    setBusy(i);
    try {
      const next = [...list];
      next[i] = await rotateFile90(list[i]);
      onChange(next);
    } finally {
      setBusy(null);
    }
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const makeMain = (i) => onChange([list[i], ...list.filter((_, idx) => idx !== i)]);

  return (
    <div className="mt-3 text-left" data-testid="new-photos-preview">
      <p className="text-xs font-semibold text-gray-600 mb-2">{list.length} foto da caricare · ⭐ scegli la principale · ↻ ruota prima di salvare</p>
      <div className="flex flex-wrap gap-3">
        {list.map((f, i) => (
          <div key={`${f.name}-${f.lastModified}-${i}`} className="relative w-24 h-24 border border-gray-200 rounded-lg bg-white overflow-hidden" data-testid={`new-photo-${i}`}>
            <img src={urls[i]} alt={f.name} className="w-full h-full object-contain" data-testid={`new-photo-img-${i}`} />
            {i === 0 && <span className="absolute top-1 left-1 text-[9px] font-bold bg-amber-400 text-white px-1 rounded">Principale</span>}
            <div className="absolute bottom-1 right-1 flex gap-1">
              {i !== 0 && (
                <button type="button" disabled={busy !== null} onClick={() => makeMain(i)} title="Imposta come foto principale" data-testid={`new-photo-main-${i}`}
                  className="bg-amber-500 hover:bg-amber-600 text-white p-1 rounded shadow disabled:opacity-50">
                  <Star className="w-3 h-3" />
                </button>
              )}
              <button type="button" disabled={busy !== null} onClick={() => rotate(i)} title="Ruota di 90°" data-testid={`new-photo-rotate-${i}`}
                className="bg-sky-600 hover:bg-sky-700 text-white p-1 rounded shadow disabled:opacity-50">
                <RotateCw className={`w-3 h-3 ${busy === i ? "animate-spin" : ""}`} />
              </button>
              <button type="button" onClick={() => remove(i)} title="Togli dalla selezione" data-testid={`new-photo-remove-${i}`}
                className="bg-red-600 hover:bg-red-700 text-white p-1 rounded shadow">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
